import DOMPurify from 'dompurify';
import parse, { domToReact } from 'html-react-parser';
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import slugify from 'slugify';
import styled from 'styled-components';
import media from '../../../constants/media';
import theme from '../../../constants/theme';

const TitleBaseH5 = styled.h5`
	font-family: ${theme.fonts.SharpSlab};
	font-weight: ${theme.fonts.weight.semiBold};
	font-size: 24px;
	letter-spacing: -0.04em;
	line-height: 1.2;
	color: ${theme.colorsV2.mainRed};
	overflow-wrap: break-word;
	scroll-margin-top: 120px;

	span {
		color: ${theme.colorsV2.mainPink};
	}

	${media.desktopSmall`
		font-size: 32px;
	`}
`;

const TitleBaseH4 = styled(TitleBaseH5)`
	font-size: 32px;
	line-height: 1;

	${media.desktopSmall`
		font-size: 40px;
	`}
`;

const TitleBaseH3 = styled(TitleBaseH5)`
	font-size: 40px;

	${media.desktopSmall`
		font-size: 48px;
	`}
`;

const TitleBaseH2 = styled(TitleBaseH5)`
	font-size: 40px;

	${media.desktopSmall`
		font-size: 64px;
	`}
`;

const TitleBaseH1 = styled(TitleBaseH5)`
	font-size: 72px;
	line-height: 1;
	font-weight: ${theme.fonts.weight.bold};

	${media.desktopSmall`
		font-size: 96px;
	`}
`;

type TTitleType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5';

const getTitleVariant = (type: TTitleType) => {
	if (type === 'h1') {
		return TitleBaseH1;
	}
	if (type === 'h2') {
		return TitleBaseH2;
	}
	if (type === 'h3') {
		return TitleBaseH3;
	}
	if (type === 'h4') {
		return TitleBaseH4;
	}
	return TitleBaseH5;
};

/**
 * DOMPurify bails out of its own setup when it cannot find a usable `document` (some privacy
 * browsers and extension sandboxes), returning an instance with `isSupported: false` and no
 * `sanitize` method at all. Strip tags ourselves in that case rather than throwing.
 */
const sanitizeHtml = (html: string): string => {
	if (typeof DOMPurify?.sanitize === 'function') return DOMPurify.sanitize(html);

	return html.replace(/<[^>]*>/g, '');
};

const transformHtml = (domNode: any, index: number): string | boolean | void | object | Element => {
	if (domNode.type === 'tag' && domNode.name === 'strong') {
		return (
			<span key={index}>{domToReact(domNode.children, { replace: transformHtml })}</span>
		) as unknown as Element;
	}
	// Return undefined to allow default processing for other nodes.
};

type TProps = {
	content: string;
	type?: TTitleType;
	className?: string;
};

export const RichTextTitle = ({ content, type = 'h2', className = null }: TProps) => {
	const RichTextTitleWrapper = getTitleVariant(type);
	const titleRef = useRef<HTMLHeadingElement>(null);
	const location = useLocation();

	useEffect(() => {
		if (!location.hash || titleRef.current?.id !== location.hash.slice(1)) {
			return;
		}
		window.scroll({
			top: titleRef.current.getBoundingClientRect().top + window.scrollY,
			behavior: 'smooth',
		});
	}, [location]);

	// Titles come straight from Umbraco and are empty whenever an editor leaves the field blank.
	const rawContent = content || '';

	// Mimic onBeforeParse: replace newlines and whitespace if needed.
	const modifiedContent = rawContent.replace(/\n/g, '<br>');
	//replace p tags with nothing
	const modifiedContentWithoutPTags = modifiedContent.replace(/<\/?p>/g, '');
	// Sanitize the modified content
	const modifiedContentWithoutHeadings = modifiedContentWithoutPTags.replace(/<\/?h[1-6]>/gi, '');

	const sanitizedContent = sanitizeHtml(modifiedContentWithoutHeadings);

	// Parse the sanitized HTML to React elements with our transform function.

	const parsedContent = parse(sanitizedContent, { replace: transformHtml });

	return (
		<RichTextTitleWrapper
			as={type}
			className={className || ''}
			ref={titleRef}
			id={slugify(rawContent.replace(/<\/?[^>]+(>|$)/g, '').replace(/\r?\n|\r/g, ''), {
				remove: /[*+~.,;()'"!?:@]/g,
			})}>
			{parsedContent}
		</RichTextTitleWrapper>
	);
};

RichTextTitle.defaultProps = {
	type: 'h2',
	className: '',
};

export default RichTextTitle;
